import { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { ERROR } from "types/snackBar.constant";
import { CSS_VARIABLES } from "enums/cssVariables";
import { getCurrentUser, performSignature } from "components/helper/callApi";
import { getAdditionalFields } from "services/additionalFields";
import { useUser } from "components/helper/userContext";
import { useSignaturePad } from "hooks/useSignaturePad";
import { useHandleErrors } from "./common/handleErrors";
import ErrorPage from "../common/ErrorPage";
import {
    DisplayedError,
    HTTPResponseError,
    NotFoundError,
    UnknownError,
} from "services/errors";
import Layout from "../common/Layout";
import Spinner from "components/common/Spinner";
import LogoutWrapper from "components/common/LogoutWrapper";
import SignatureLogoutButton from "./common/SignatureLogoutButton";
import ProxiesAccordion from "../vote/Votebox/common/AutomaticVoteForProxiesComponents/ProxiesAccordion";

import type { User } from "types/User";

const maxWidth = 400;
const height = 200;

const Signature = () => {
    const { t } = useTranslation();
    const { user, setUser } = useUser();
    const { enqueueSnackbar } = useSnackbar();
    const { handleLoginError } = useHandleErrors();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<DisplayedError | null>(null);

    const {
        canvasRef,
        underLayerCanvasRef,
        isEmpty,
        clear,
        isSignatureDrawn,
        getSignatureBlob,
    } = useSignaturePad({
        placeholderLines: [t("signature.signHere"), user?.displayName ?? ""],
    });

    async function handleValidate() {
        if (isSubmitting) {
            return;
        }

        // A mobile resize can wipe the canvas after the last stroke
        if (!isSignatureDrawn()) {
            enqueueSnackbar(t("signature.emptyOrTooSmall"), {
                variant: ERROR,
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const blob = await getSignatureBlob();

            if (!blob) {
                enqueueSnackbar(t("errorUnknown"), { variant: ERROR });
                return;
            }

            await sendSignature(blob);
        } finally {
            setIsSubmitting(false);
        }
    }

    async function sendSignature(blob: Blob) {
        let signedUser: User | null;

        try {
            signedUser = await performSignature(blob);
        } catch (error) {
            handleSendError(error);
            return;
        }

        // `PUT /user/me/sign` may omit isSigned, the field the guard checks
        if (signedUser?.isSigned === undefined) {
            try {
                signedUser = await getCurrentUser(getAdditionalFields({}));
            } catch {
                // The signature is stored: only the refresh failed
                setUser((oldUser) =>
                    oldUser ? { ...oldUser, isSigned: true } : oldUser,
                );
                return;
            }
        }

        // Accepted but still unsigned: the guard would show a mute pad
        if (!signedUser?.isSigned) {
            enqueueSnackbar(t("errorUnknown"), { variant: ERROR });
            return;
        }

        setUser(signedUser);
    }

    function handleSendError(error: unknown) {
        if (!(error instanceof HTTPResponseError)) {
            setError(error as NotFoundError | UnknownError);
            return;
        }

        // Signature refused by the backend: blank or unreadable image
        if (error.response.status === 400) {
            enqueueSnackbar(t("signature.emptyOrTooSmall"), {
                variant: ERROR,
            });
            return;
        }

        // handleLoginError throws: without this the rejection escapes unnoticed
        try {
            handleLoginError(error.response.status);
        } catch (displayedError) {
            setError(displayedError as NotFoundError | UnknownError);
        }
    }

    const hasProxies = Boolean(user && user.proxies.length > 0);

    if (error) {
        return <ErrorPage error={error} />;
    }

    return (
        <>
            <LogoutWrapper>
                <SignatureLogoutButton />
            </LogoutWrapper>
            <Layout>
                <>
                    <Wrapper>
                        <StyledCanvasWrapper>
                            <StyledCanvas
                                ref={canvasRef}
                                style={{ zIndex: "1" }}
                                aria-label={t("canvasSignature")}
                                role="region"
                            />
                            <StyledCanvas
                                ref={underLayerCanvasRef}
                                style={{ zIndex: "0", border: "none" }}
                                aria-hidden="true"
                            />
                        </StyledCanvasWrapper>
                        <ButtonWrapper>
                            <StyledOutlineButton
                                onClick={clear}
                                aria-label={t("clear")}
                                disabled={isEmpty || isSubmitting}
                            >
                                {t("clear")}
                            </StyledOutlineButton>
                            <StyledButton
                                onClick={handleValidate}
                                disabled={isEmpty || isSubmitting}
                                aria-label={t("validate")}
                            >
                                {t("validate")}
                            </StyledButton>
                        </ButtonWrapper>
                    </Wrapper>
                    {hasProxies && (
                        <ProxiesAccordion>
                            <StyledWrapperProxies>
                                {user?.proxies.map((proxy) => {
                                    return (
                                        <div key={proxy.id}>
                                            <p>{proxy.displayName}</p>
                                        </div>
                                    );
                                })}
                            </StyledWrapperProxies>
                        </ProxiesAccordion>
                    )}
                </>
            </Layout>
            {/* Overlaid, not an early return, so the canvas stays mounted */}
            {isSubmitting && (
                <SpinnerOverlay
                    role="status"
                    aria-live="polite"
                    aria-label={t("loading")}
                >
                    <Spinner />
                </SpinnerOverlay>
            )}
        </>
    );
};

const Wrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    margin: auto;
    max-width: ${maxWidth}px;
    > p {
        margin: auto;
        padding: 2rem 0 1rem 0;
        font-size: 0.75rem;
        line-height: 22px;
        text-align: left;
        width: 620px;
        > a {
            text-decoration: underline;
            color: black;
        }
        @media (max-width: 768px) {
            width: 320px;
        }
    }
`;

const ButtonWrapper = styled.div`
    display: flex;
    width: 100%;
    justify-content: end;
`;

const StyledWrapperProxies = styled.div`
    padding-left: 1.5rem;
`;

const StyledCanvasWrapper = styled.div`
    position: relative;
    width: 100%;
    height: ${height}px;
`;

const StyledCanvas = styled.canvas`
    border: 1px solid gray;
    cursor: crosshair;
    position: absolute;
    height: 100%;
    width: 100%;
`;

const SpinnerOverlay = styled.div`
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.6);
    z-index: 2000;
`;

const StyledOutlineButton = styled.button.attrs({ type: "button" })`
    padding: 10px 18px;
    margin: 0.5rem 0.5rem;
    font-size: 18px;
    cursor: pointer;
    background-color: #fafafa;
    border: 1px solid var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    color: var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    :focus-visible {
        outline: solid 2px var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    }
    &:hover {
        filter: brightness(0.8);
    }

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
        filter: none;
        pointer-events: none;
    }
`;

const StyledButton = styled(StyledOutlineButton)`
    background-color: var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    border: 1px solid var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    color: #fff;
    margin-right: 0;
    :focus-visible {
        outline: solid 2px var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    }
`;

export default Signature;
